/**
 * imageMapping.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

// Smart image mapping utility for products
export const getProductImage = (product) => {
  // Check if product exists and has required properties
  if (!product) {
    return '/images/placeholder.jpg';
  }

  // If product has an image, use it
  if (product.image) {
    const imageValue = String(product.image).trim();

    // Numeric asset ID (same as backend formatProductImageUrl)
    if (/^\d+$/.test(imageValue)) {
      return `/api/assets/${imageValue}/thumbnail`;
    }

    // Handle full URLs (from database assets API)
    if (imageValue.startsWith('http://') || imageValue.startsWith('https://')) {
      return imageValue;
    }
    
    const apiAsset = imageValue.match(/\/api\/assets\/(\d+)\/(?:download|thumbnail|view)/);
    if (apiAsset) {
      return `/api/assets/${apiAsset[1]}/thumbnail`;
    }

    const staticAsset = imageValue.split('?')[0].match(/\/assets\/.+_(\d{3,})\.[a-z0-9]+$/i);
    if (staticAsset) {
      return `/api/assets/${staticAsset[1]}/thumbnail`;
    }
    
    // Handle database asset filenames (timestamp prefix, UUID, or uploads path)
    if (product.image.match(/^\d{13}-[a-z0-9]+-/) || 
        product.image.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/) ||
        product.image.includes('uploads/')) {
      // For database assets, construct the asset API URL
      return `/api/assets/${product.image}/thumbnail`;
    }
    
    if (product.image.startsWith('product_images_consolidated/')) {
      return `/images/products/${product.image.replace('product_images_consolidated/', '')}`;
    }
    if (product.image.startsWith('/')) {
      return product.image;
    }
    return `/images/products/${product.image}`;
  }

  // Map product names to available images
  const name = (product.name || '').toLowerCase();
  const category = product.category?.toLowerCase() || '';
  
  // Cape Breton books
  if (category.includes('cape breton') || category.includes('celtic music')) {
    if (name.includes('brenda stubbert')) {
      return name.includes('2nd') ? '/images/products/cape_breton/stubbert2nd.jpg' : '/images/products/cape_breton/Book-BrendaStubbert.jpg';
    }
    if (name.includes('jerry holland')) {
      return name.includes('2nd') ? '/images/products/cape_breton/Book-JerryHolland2.jpg' : '/images/products/cape_breton/Book-JerryHolland1.jpg';
    }
    if (name.includes('lighthouse')) {
      return '/images/products/cape_breton/Book-Lighthouse.jpg';
    }
    if (name.includes('winston')) {
      return '/images/products/cape_breton/Book-Winston.jpg';
    }
    if (name.includes('heather hill')) {
      return '/images/products/cape_breton/book-HeatherHill.jpg';
    }
    if (name.includes('trip to windsor')) {
      return '/images/products/cape_breton/Book-TripToWindsor.jpg';
    }
    if (name.includes('alister macgillivray')) {
      return '/images/products/cape_breton/book-alistermacgillivray.jpg';
    }
    if (name.includes('kinnon beaton')) {
      return '/images/products/cape_breton/Kinnon105.JPG';
    }
    if (name.includes('andrea beaton')) {
      return '/images/products/cape_breton/Andrea-Tunes.jpg';
    }
    if (name.includes('fiddletree')) {
      return '/images/products/cape_breton/Fiddletree.jpg';
    }
    if (name.includes('celtic colours')) {
      return '/images/products/cape_breton/CelticColours.jpg';
    }
    if (name.includes('campbell')) {
      return '/images/products/cape_breton/Campbell_Legacy.jpg';
    }
    if (name.includes('cape breton fiddler')) {
      return '/images/products/cape_breton/CBFC_Cover.jpg';
    }
    if (name.includes('cape breton highland')) {
      return '/images/products/cape_breton/CBHC.JPG';
    }
    if (name.includes('cape breton scottish')) {
      return '/images/products/cape_breton/CBSC.jpg';
    }
    if (name.includes('gathering of the clans')) {
      return '/images/products/cape_breton/book-barryshearsvol1.jpg';
    }
    if (name.includes('barry shears')) {
      return '/images/products/cape_breton/book-barryshearsvol1.jpg';
    }
    if (name.includes('canadian fiddle')) {
      return '/images/products/canadian/book-CFM1.jpg';
    }
    if (name.includes('dan r macdonald')) {
      return '/images/products/cape_breton/Book-TripToWindsor.jpg';
    }
  }
  
  // Irish books
  if (category.includes('irish') || name.includes('irish')) {
    if (name.includes('allan')) {
      return '/images/products/irish/Book-Allans.jpg';
    }
    if (name.includes('sean ryan')) {
      return '/images/products/irish/Book-SeanRyan.jpg';
    }
    if (name.includes('ryan')) {
      return '/images/products/irish/Book-Ryans.jpg';
    }
    if (name.includes('langan')) {
      return '/images/products/irish/book-LanganCover.GIF';
    }
    if (name.includes('packie')) {
      return '/images/products/irish/book-PackieManus.jpg';
    }
    if (name.includes('musical memories')) {
      return '/images/products/irish/Book-MusicalMemories.jpg';
    }
    if (name.includes('essential guide')) {
      return '/images/products/irish/bk-EssentialFlute.jpg';
    }
    if (name.includes('handy with the stick')) {
      return '/images/products/irish/HandyStick.JPG';
    }
    if (name.includes('turoe stone')) {
      return '/images/products/irish/turoeStone.JPG';
    }
    if (name.includes('miller') && name.includes('perron')) {
      return '/images/products/irish/Miller-Perron.jpg';
    }
  }
  
  // Scottish books
  if (category.includes('scottish') || name.includes('scottish')) {
    if (name.includes('skye')) {
      return '/images/products/scottish/Book-Skye.jpg';
    }
    if (name.includes('simon fraser')) {
      return '/images/products/scottish/Book-SimonFraser.jpg';
    }
    if (name.includes('skinner')) {
      return '/images/products/scottish/Book-Skinner.jpg';
    }
    if (name.includes('harp and claymore')) {
      return '/images/products/scottish/book-harpclaymore.jpg';
    }
    if (name.includes('marshall')) {
      return '/images/products/scottish/Marshall.jpg';
    }
    if (name.includes('glengarry')) {
      return '/images/products/scottish/glengarry.JPG';
    }
  }
  
  // Pipe music
  if (category.includes('pipe') || name.includes('pipe')) {
    if (name.includes('shears')) {
      return '/images/products/pipe_music/book-barryshearsvol1.jpg';
    }
  }
  
  // CDs
  if (category.includes('cd') || name.includes('cd')) {
    if (name.includes('classic cuts')) {
      return '/images/products/cape_breton/BookCD_Winston.jpg';
    }
    if (name.includes('crystal clear')) {
      return '/images/products/cape_breton/Holland1FC.jpg';
    }
    if (name.includes('fiddler')) {
      return '/images/products/cape_breton/Holland1FC.jpg';
    }
  }
  
  // Default fallback based on category
  if (category.includes('cape breton') || category.includes('celtic music')) {
    return '/images/products/cape_breton/Book-Lighthouse.jpg';
  }
  if (category.includes('irish')) {
    return '/images/products/irish/Book-Allans.jpg';
  }
  if (category.includes('scottish')) {
    return '/images/products/scottish/Book-Skye.jpg';
  }
  
  // Final fallback
  return '/images/placeholder.jpg';
};
